import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type {
  SendQuestionnaireQuestion,
  SendQuestionnaireQuestionWithId,
} from '@/_constants/questionnaires';

/**
 * Hook for managing an ordered list of questionnaire questions
 */
export const useItemList = () => {
  const createItemWithId = useCallback(
    (): SendQuestionnaireQuestionWithId => ({
      id: uuidv4(),
      options: [],
      question_type: 'upload',
      section_id: 0,
      text: '',
    }),
    [],
  );

  const [items, setItems] = useState<Array<SendQuestionnaireQuestionWithId>>(
    () => Array.from({ length: 1 }, createItemWithId),
  );

  /**
   * Add a new item to the end of the list
   */
  const addItem = useCallback(() => {
    setItems((prev) => [...prev, createItemWithId()]);
  }, [createItemWithId]);

  /**
   * Remove an item by its ID
   */
  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  /**
   * Update an item by its ID
   */
  const updateItem = useCallback(
    (id: string, updates: Partial<SendQuestionnaireQuestion>) => {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
      );
    },
    [],
  );

  /**
   * Reorder items by moving from one index to another
   */
  const reorderItems = useCallback((fromIndex: number, toIndex: number) => {
    setItems((prev) => {
      const newItems = [...prev];
      const [movedItem] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, movedItem);
      return newItems;
    });
  }, []);

  /**
   * Reset to a single new item
   */
  const resetItems = useCallback(() => {
    setItems([createItemWithId()]);
  }, [createItemWithId]);

  /**
   * Get items without internal IDs (for API calls, etc.)
   */
  const getItems = useCallback((): Array<SendQuestionnaireQuestion> => {
    return items.map(
      ({ id: _id, ...item }) => item as SendQuestionnaireQuestion,
    );
  }, [items]);

  return {
    items,
    addItem,
    removeItem,
    updateItem,
    reorderItems,
    resetItems,
    getItems,
    count: items.length,
    isEmpty: items.length === 0,
  };
};
